<template>
  <div id="credit-transaction" class="flex h-full bg-pure-white">
    <div class="flex flex-col flex-grow pt-2">
      <div
        id="credit-title"
        class="sticky z-50 flex items-center justify-between top-18 left-6 h-18 bg-pure-white"
        :style="{ width: 'calc(100vw - 3rem - 0.5rem)' }"
      >
        <div class="flex flex-col gap-1">
          <div class="flex items-center gap-2">
            <div
              class="px-2 py-2 border border-slate-400"
              :style="{ borderRadius: '4px', backgroundColor: '#F4F3FF' }"
            >
              <BlIcon icon="piggy" class="w-6 h-6" />
            </div>
            <BlTextTitle4 text="Credits" />
          </div>
        </div>
        <div class="flex items-center gap-3">
          <div class="flex items-center gap-2">
            <BlSort
              v-model="sortInput"
              :options="sortOptions"
              :defaultValue="{ primary: 'date', secondary: 'desc' }"
              @change="onChangeSort($event)"
            />
            <BlInputFilterAdvance
              bubbleCount
              @changed="onChangeFilter"
              :input="filterInput"
            />
          </div>
          <div class="h-10 w-0.5 bg-slate-300"></div>
          <div class="flex gap-2">
            <BlInputButton
              type="outlined"
              label="Export as PDF"
              @clicked="exportOpened = true"
            />
            <BlInputButton
              v-if="currentUser.role.includes('admin')"
              icon="plus"
              label="Credit transaction"
              @clicked="newTransactionOpened = true"
            />
          </div>
        </div>
      </div>

      <ExportCreditTransactionModal
        v-if="exportOpened"
        @close-modal="exportOpened = false"
      />
      <NewCreditTransactionModal
        v-if="newTransactionOpened"
        @after-commit="fetchCreditTransactions"
        @close-modal="newTransactionOpened = false"
      />

      <div
        v-if="!pageLoading && creditDetails"
        id="credit-details"
        class="sticky flex gap-3 mb-4 left-6"
        :style="{ width: 'calc(100vw - 3rem - 0.5rem)' }"
      >
        <CreditDetails
          title="Available session credits"
          :expiringSoon="creditDetails.session_credits.expiring_soon"
          :available="creditDetails.session_credits.available"
          :used="creditDetails.session_credits.used"
          usePanel
          @open-panel="onOpenPanel('available')"
        />
        <CreditDetails
          title="Reschedule credits"
          :expiringSoon="creditDetails.reschedule_credits.expiring_soon"
          :available="creditDetails.reschedule_credits.available"
          :used="creditDetails.reschedule_credits.used"
          usePanel
          @open-panel="onOpenPanel('reschedule')"
        />
        <CreditDetails
          title="Scheduled sessions"
          :totalSessions="creditDetails.scheduled_credits.total_sessions"
          :totalCredits="creditDetails.scheduled_credits.total_credits"
          usePanel
          @open-panel="onOpenPanel('scheduled')"
        />
        <CreditDetails
          title="Current balance"
          :currentBalance="creditDetails.current_balance"
        />
      </div>

      <div v-if="pageLoading" class="grid h-128 place-items-center">
        <div class="blum-loader"></div>
      </div>
      <div
        v-else-if="!creditTransactions.length"
        class="grid h-128 place-items-center"
      >
        <BlTextBody1
          text="All credit transactions will be displayed here."
          class="text-center"
        />
      </div>
      <div v-else class="mb-4">
        <div
          id="credit-header"
          class="sticky z-20 flex items-center rounded-t top-36"
        >
          <div class="sticky left-0 z-10 flex pl-6 bg-pure-white">
            <BlTooltip
              class="flex items-center flex-shrink-0 w-20 h-12 px-4 border-t border-l rounded-tl bg-slate-300 border-slate-400"
            >
              <BlTextSubtitle3 text="ID" class="truncate" />
            </BlTooltip>
            <BlTooltip
              class="flex items-center flex-shrink-0 w-40 h-12 px-4 border-t border-r bg-slate-300 border-slate-400"
            >
              <BlTextSubtitle3 text="Date created" class="truncate" />
            </BlTooltip>
          </div>
          <BlTooltip
            class="flex items-center flex-shrink-0 h-12 px-4 border-t w-50 bg-slate-300 border-slate-400"
          >
            <BlTextSubtitle3 text="Accountable" class="truncate" />
          </BlTooltip>
          <BlTooltip
            class="flex items-center flex-shrink-0 h-12 px-4 border-t w-35 bg-slate-300 border-slate-400"
          >
            <BlTextSubtitle3 text="Type" class="truncate" />
          </BlTooltip>
          <BlTooltip
            class="flex items-center flex-shrink-0 h-12 px-4 border-t w-50 bg-slate-300 border-slate-400"
          >
            <BlTextSubtitle3 text="Item" class="truncate" />
          </BlTooltip>
          <BlTooltip
            class="flex items-center flex-shrink-0 h-12 px-4 border-t w-35 bg-slate-300 border-slate-400"
          >
            <BlTextSubtitle3 text="Session#" class="truncate" />
          </BlTooltip>
          <BlTooltip
            class="flex items-center flex-shrink-0 h-12 px-4 border-t w-35 bg-slate-300 border-slate-400"
          >
            <BlTextSubtitle3 text="Reschedule#" class="truncate" />
          </BlTooltip>
          <BlTooltip
            class="flex items-center flex-shrink-0 h-12 px-4 border-t w-50 bg-slate-300 border-slate-400"
          >
            <BlTextSubtitle3 text="Amount" class="truncate" />
          </BlTooltip>
          <BlTooltip
            class="flex items-center flex-shrink-0 h-12 px-4 border-t w-35 bg-slate-300 border-slate-400"
          >
            <template v-slot:tooltip>
              <div class="flex flex-col w-40 gap-1 text-center">
                The expiration date is the deadline to use credits for booking;
                once booked, they stay valid even if the appointment occurs
                later.
              </div>
            </template>
            <BlTextSubtitle3 text="Expiration date" class="truncate" />
          </BlTooltip>
          <BlTooltip
            class="flex items-center flex-grow flex-shrink-0 h-12 px-4 border-t w-100 bg-slate-300 border-slate-400"
          >
            <BlTextSubtitle3 text="Reason" class="truncate" />
          </BlTooltip>
          <div class="sticky right-0 z-10 flex pr-6 bg-pure-white">
            <div
              class="flex items-center flex-shrink-0 h-12 px-4 border-t border-r rounded-tr w-30 bg-slate-300 border-slate-400"
            >
              <BlTextSubtitle3 text="" />
            </div>
          </div>
        </div>

        <CreditItem
          v-for="(credit, idx) in creditTransactions"
          :key="credit.id"
          :credit="credit"
          :isLast="idx + 1 === creditTransactions.length"
        />
      </div>

      <div v-if="totalCreditTransactions > 25" class="flex mb-36">
        <BlInputPagination
          class="sticky left-6"
          :style="{ width: 'calc(100vw - 3rem - 0.5rem)' }"
          :page="page"
          :perPage="perPage"
          :total="totalCreditTransactions"
          @changed="changePage"
          @changed-per-page="changePerPage"
        />
      </div>
      <div v-else class="mb-46"></div>
    </div>

    <div
      :data-id="panelId"
      id="panel-details"
      class="fixed top-0 flex flex-col overflow-y-auto transition-all duration-300 border w-116 bg-pure-white border-slate-400"
      :class="[panelOpened ? 'right-0' : '-right-116']"
      :style="{ height: 'calc(100vh)', zIndex: 101 }"
    >
      <AvailableCreditsPanel
        v-if="panelSection === 'available'"
        :panelId="panelId"
      />
      <RescheduleCreditsPanel
        v-if="panelSection === 'reschedule'"
        :panelId="panelId"
      />
      <ScheduledSessionsPanel
        v-if="panelSection === 'scheduled'"
        :panelId="panelId"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { displayDate, displayPrice } from "@/plugins/helper.js";

import ExportCreditTransactionModal from "./creditTransaction/ExportCreditTransactionModal.vue";
import NewCreditTransactionModal from "./creditTransaction/NewCreditTransactionModal.vue";
import CreditDetails from "./creditTransaction/CreditDetails.vue";
import AvailableCreditsPanel from "./creditTransaction/AvailableCreditsPanel.vue";
import RescheduleCreditsPanel from "./creditTransaction/RescheduleCreditsPanel.vue";
import ScheduledSessionsPanel from "./creditTransaction/ScheduledSessionsPanel.vue";
import CreditItem from "./creditTransaction/Item.vue";

import BlTextTitle4 from "../BlTextTitle4.vue";
import BlTextSubtitle3 from "../BlTextSubtitle3.vue";
import BlTextBody1 from "../BlTextBody1.vue";
import BlTextBody2 from "../BlTextBody2.vue";
import BlTooltip from "../BlTooltip.vue";
import BlIcon from "../BlIcon.vue";
import BlChip from "../BlChip.vue";
import BlInputButton from "../BlInputButton.vue";
import BlInputPagination from "../BlInputPagination.vue";
import BlSort from "../BlSort.vue";
import BlInputFilterAdvance from "../BlInputFilterAdvance.vue";

export default {
  name: "ClientCreditTransaction",
  components: {
    ExportCreditTransactionModal,
    NewCreditTransactionModal,
    CreditDetails,
    AvailableCreditsPanel,
    RescheduleCreditsPanel,
    ScheduledSessionsPanel,
    CreditItem,
    //
    BlTextTitle4,
    BlTextSubtitle3,
    BlTextBody1,
    BlTextBody2,
    BlTooltip,
    BlIcon,
    BlChip,
    BlInputButton,
    BlInputPagination,
    BlSort,
    BlInputFilterAdvance
  },
  props: ["id"],
  data() {
    return {
      pageLoading: true,
      page: 1,
      perPage: 25,
      exportOpened: false,
      newTransactionOpened: false,
      sortInput: { primary: "date", secondary: "desc" },
      sortOptions: {
        primary: [{ value: "date", label: "Date created" }],
        secondary: [
          { value: "asc", label: "Ascending" },
          { value: "desc", label: "Descending" }
        ]
      },
      filterInput: {
        model: {
          date: null,
          type: []
        },
        tabs: [
          { name: "Date created", kind: "date" },
          {
            name: "Type",
            kind: "type",
            options: [
              {
                name: "Addition",
                value: "CreditTransaction::Addition",
                chip: true
              },
              {
                name: "Subtraction",
                value: "CreditTransaction::Subtraction",
                chip: true
              }
            ]
          }
        ]
      },
      //
      panelId: "client-credit-panel",
      panelOpened: false,
      panelSection: "" // available, reschedule, scheduled
    };
  },
  computed: {
    ...mapGetters([
      "currentUser",
      "currentCenter",
      "creditTransactionDetails",
      "creditTransactions",
      "totalCreditTransactions"
    ]),
    getParams() {
      let params = `?page=${this.page}&per_page=${this.perPage}`;
      const sort = this.sortInput;
      // - sort mapping -
      // date created, ascending -> oldest
      // date created, descending -> newest
      if (sort.primary === "date") {
        params += `&sort=${sort.secondary === "desc" ? "newest" : "oldest"}`;
      }
      const date = this.filterInput.model.date;
      if (date) {
        if (typeof date === "string") {
          const s = {
            last_week: "last_7_days",
            last_month: "last_30_days",
            last_year: "last_year"
          };
          params += `&filter_by=${s[date]}`;
        } else {
          if (date.start_date) {
            params += `&start_date=${date.start_date}`;
          }
          if (date.end_date) {
            params += `&end_date=${date.end_date}`;
          }
        }
      }
      const types = this.filterInput.model.type;
      if (types.length > 0) {
        params += `&type=${types.join(",")}`;
      }
      return params;
    },
    creditDetails() {
      if (!this.creditTransactionDetails) return null;
      return this.creditTransactionDetails.credit_detail || null;
    }
  },
  watch: {
    panelOpened(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closePanelHandler);
        }, 10);
      }
    }
  },
  async mounted() {
    const searchParams = this.$route.query;
    if (searchParams["page"]) {
      this.page = Number(searchParams["page"]);
    }
    if (searchParams["per_page"]) {
      this.perPage = Number(searchParams["per_page"]);
    }
    if (searchParams["sort"]) {
      const temp = searchParams["sort"] || "";
      if (temp === "newest") {
        this.sortInput = { primary: "date", secondary: "desc" };
      } else if (temp === "oldest") {
        this.sortInput = { primary: "date", secondary: "asc" };
      }
    }
    if (searchParams["filter_by"]) {
      const s = {
        last_7_days: "last_week",
        last_30_days: "last_month",
        last_year: "last_year"
      };
      this.filterInput.model.date = s[searchParams["filter_by"]] | null;
    }
    if (searchParams["start_date"] || searchParams["end_date"]) {
      this.filterInput.model.date = {
        start_date: searchParams["start_date"] || "",
        end_date: searchParams["end_date"] || ""
      };
    }
    if (searchParams["type"]) {
      const arr = searchParams["type"].split(",");
      this.filterInput.model.type = arr;
    }
    this.pageLoading = true;
    await this.getCreditTransactionDetails({ client_id: this.id });
    this.fetchCreditTransactions();
  },
  methods: {
    ...mapActions(["getCreditTransactionDetails", "getCreditTransactions"]),
    displayDate,
    displayPrice,
    //
    async fetchCreditTransactions() {
      const params = { client_id: this.id, params: this.getParams };

      this.pageLoading = true;
      await this.getCreditTransactions(params);
      this.pageLoading = false;

      // sync url path for page
      const incomingPath = `/clients/${this.id}/credit${this.getParams}`;
      if (this.$route.fullPath !== incomingPath) {
        return this.$router.push(incomingPath);
      }
    },
    onChangeSort(val) {
      const { primary, secondary } = this.sortInput;
      if (val.primary !== primary || val.secondary !== secondary) {
        this.sortInput = val;
        this.page = 1;
        this.fetchCreditTransactions();
      }
    },
    onChangeFilter(payload) {
      this.filterInput.model = payload.value;
      this.page = 1;
      this.fetchCreditTransactions();
    },
    changePage(page) {
      if (page === this.page) return;
      this.page = page;
      this.fetchCreditTransactions();
    },
    changePerPage(perPage) {
      if (perPage === this.perPage) return;
      this.page = 1;
      this.perPage = perPage;
      this.fetchCreditTransactions();
    },
    //
    displayItem(credit) {
      const services = credit.credit_transaction_services || [];
      if (!services.length) return "";
      if (services.length === 1) return services[0].package_item_name;
      return `Mixed items (${services.length})`;
    },
    displaySession(credit) {
      if (credit.type === "CreditTransaction::Addition") {
        if (credit.session_credit === 0) return "0";
        return `+${credit.session_credit}`;
      }
      if (credit.type === "CreditTransaction::Subtraction") {
        if (credit.session_credit === 0) return "";
        if (credit.session_credit === null) return "";
        return `(${credit.session_credit})`;
      }
    },
    displayReschedule(credit) {
      if (credit.type === "CreditTransaction::Addition") {
        if (credit.reschedule_credit === 0) return "0";
        if (credit.reschedule_credit === null) return "Unlimited";
        return `+${credit.reschedule_credit}`;
      }
      if (credit.type === "CreditTransaction::Subtraction") {
        if (credit.reschedule_credit === 0) return "";
        if (credit.reschedule_credit === null) return "";
        return `(${credit.reschedule_credit})`;
      }
    },
    displayAmount(credit) {
      if (credit.amount === 0) return "0";
      const settings = this.currentCenter.currency_settings;
      const amount = this.displayPrice({ settings, price: credit.amount });
      if (credit.type === "CreditTransaction::Subtraction") {
        return `(${amount})`;
      }
      return amount;
    },
    displayExpirationDate(credit) {
      if (credit.type === "CreditTransaction::Subtraction") return "";
      if (!credit.expiration_date) return "No expiration date";
      return this.displayDate({
        date: credit.expiration_date,
        format: "DD MMM YYYY"
      });
    },
    //
    onOpenPanel(section) {
      const timeout = this.panelOpened ? 300 : 0;
      setTimeout(async () => {
        this.panelSection = section;
        this.panelOpened = true;
      }, timeout);
    },
    closePanelHandler(e) {
      let close = true;
      if (e.target && e.target.dataset && e.target.dataset.id) {
        const id = e.target.dataset.id || "";
        if (id === this.panelId) close = false;
      }
      if (close) {
        window.removeEventListener("click", this.closePanelHandler);
        this.panelOpened = false;
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/clients/ClientCredit.vue